const RELOAD_COOLDOWN_MS = 5 * 60 * 1000;
const STORAGE_KEY_PREFIX = 'crest-v2:stale-dynamic-import-reload:';

const DYNAMIC_IMPORT_ERROR_PATTERNS = [
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
  /error loading dynamically imported module/i,
  /Failed to load module script/i,
  /ChunkLoadError/i,
  /Loading chunk [\w-]+ failed/i,
  /Unable to preload CSS/i,
  /MIME type .*text\/html/i,
];

let installed = false;

export function installStaleDynamicImportReloadHandler() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('vite:preloadError', handleVitePreloadError as EventListener);
  window.addEventListener('error', handleWindowError, true);
  window.addEventListener('unhandledrejection', handleUnhandledRejection);
}

export function isDynamicImportLoadError(reason: unknown): boolean {
  const message = extractErrorMessage(reason);
  return DYNAMIC_IMPORT_ERROR_PATTERNS.some((pattern) => pattern.test(message));
}

export function reloadOnceForStaleDynamicImport(now = Date.now()): boolean {
  if (typeof window === 'undefined') return false;

  const storageKey = `${STORAGE_KEY_PREFIX}${window.location.pathname}${window.location.search}`;
  const lastReloadAt = readSessionTimestamp(storageKey);
  if (lastReloadAt && now - lastReloadAt < RELOAD_COOLDOWN_MS) return false;

  writeSessionTimestamp(storageKey, now);
  window.location.reload();
  return true;
}

function handleVitePreloadError(event: Event) {
  event.preventDefault();
  reloadOnceForStaleDynamicImport();
}

function handleWindowError(event: ErrorEvent) {
  const reason = event.error ?? event.message;
  if (!isDynamicImportLoadError(reason)) return;
  if (reloadOnceForStaleDynamicImport()) {
    event.preventDefault();
  }
}

function handleUnhandledRejection(event: PromiseRejectionEvent) {
  if (!isDynamicImportLoadError(event.reason)) return;
  if (reloadOnceForStaleDynamicImport()) {
    event.preventDefault();
  }
}

function extractErrorMessage(reason: unknown): string {
  if (reason instanceof Error) return `${reason.name}: ${reason.message}`;
  if (typeof reason === 'string') return reason;
  if (reason && typeof reason === 'object') {
    const maybeMessage = (reason as { message?: unknown }).message;
    if (typeof maybeMessage === 'string') return maybeMessage;
  }
  return String(reason ?? '');
}

function readSessionTimestamp(key: string): number | null {
  try {
    const value = window.sessionStorage.getItem(key);
    if (!value) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  } catch {
    return null;
  }
}

function writeSessionTimestamp(key: string, value: number) {
  try {
    window.sessionStorage.setItem(key, String(value));
  } catch {
    // sessionStorage 不可用时仍然刷新，最多依赖浏览器自身停止异常循环。
  }
}
